// Minimal 360 cubemap viewer in TypeScript with 16:9 aspect ratio + letterboxing
export function initInterior360(root: HTMLElement) {
    const canvas = document.createElement("canvas");
    canvas.width = 1120;
    canvas.height = 1120;
    canvas.style.display = "block"; // remove inline gaps
    canvas.style.margin = "auto";   // center if letterboxed
    root.appendChild(canvas);


    // --- Add toggle button ---
    // --- Use existing button in markup ---
    const toggleBtn = document.querySelector<HTMLButtonElement>("[data-interior-controls]");
    let controlsEnabled = false;

    if (toggleBtn) {
        toggleBtn.addEventListener("click", () => {
            controlsEnabled = !controlsEnabled;

            toggleBtn.classList.toggle("active");
            autoRotate = true;
            //toggleBtn.textContent = controlsEnabled ? "Disable Controls" : "Enable Controls";
        });
    }

    const gl = canvas.getContext("webgl");
    if (!gl) throw new Error("WebGL not supported");

    const vsSource = `
    attribute vec3 aPosition;
    uniform mat4 uViewMatrix;
    uniform float uZoom;
    uniform float uAspect;
    varying vec3 vDirection;
    void main() {
      vec3 scaled = aPosition * vec3(uZoom * uAspect, uZoom, 1.0);
      vDirection = mat3(uViewMatrix) * scaled;
      gl_Position = vec4(aPosition.x * uAspect, aPosition.y, aPosition.z, 1.0);
    }
  `;

    const fsSource = `
    precision mediump float;
    varying vec3 vDirection;
    uniform samplerCube uCubeMap;
    void main() {
      gl_FragColor = textureCube(uCubeMap, normalize(vDirection));
    }
  `;

    function compileShader(type: number, source: string): WebGLShader {
        const shader = gl.createShader(type)!;
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            throw new Error(gl.getShaderInfoLog(shader) || "Shader compile failed");
        }
        return shader;
    }

    function createProgram(vs: string, fs: string): WebGLProgram {
        const vertexShader = compileShader(gl.VERTEX_SHADER, vs);
        const fragmentShader = compileShader(gl.FRAGMENT_SHADER, fs);
        const program = gl.createProgram()!;
        gl.attachShader(program, vertexShader);
        gl.attachShader(program, fragmentShader);
        gl.linkProgram(program);
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            throw new Error(gl.getProgramInfoLog(program) || "Program link failed");
        }
        return program;
    }

    const program = createProgram(vsSource, fsSource);
    gl.useProgram(program);

    const vertices = new Float32Array([
        -1, -1, 1,
        -1, 1, 1,
        1, 1, 1,
        1, -1, 1,
    ]);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

    const aPosition = gl.getAttribLocation(program, "aPosition");
    gl.enableVertexAttribArray(aPosition);
    gl.vertexAttribPointer(aPosition, 3, gl.FLOAT, false, 0, 0);

    const faces = [
        { target: gl.TEXTURE_CUBE_MAP_POSITIVE_X, url: root.dataset.posx! },
        { target: gl.TEXTURE_CUBE_MAP_NEGATIVE_X, url: root.dataset.negx! },
        { target: gl.TEXTURE_CUBE_MAP_POSITIVE_Y, url: root.dataset.posy! },
        { target: gl.TEXTURE_CUBE_MAP_NEGATIVE_Y, url: root.dataset.negy! },
        { target: gl.TEXTURE_CUBE_MAP_POSITIVE_Z, url: root.dataset.posz! },
        { target: gl.TEXTURE_CUBE_MAP_NEGATIVE_Z, url: root.dataset.negz! },
    ];

    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_CUBE_MAP, texture);

    let imagesLoaded = 0;
    let readyToRender = false;

    faces.forEach(({ target, url }) => {
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.src = url;
        image.onload = () => {
            gl.bindTexture(gl.TEXTURE_CUBE_MAP, texture);
            gl.texImage2D(target, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
            imagesLoaded++;
            if (imagesLoaded === 6) {
                gl.generateMipmap(gl.TEXTURE_CUBE_MAP);
                readyToRender = true;
            }
        };
    });

    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    const uViewMatrix = gl.getUniformLocation(program, "uViewMatrix");
    const uZoom = gl.getUniformLocation(program, "uZoom");
    const uAspect = gl.getUniformLocation(program, "uAspect");

    let yaw = 0;
    let pitch = 0;
    let dragging = false;
    let lastX = 0, lastY = 0;
    let velocityX = 0, velocityY = 0;
    let zoom = 1;
    const defaultZoom = 1;
    let autoRotate = true;
    const autoRotateSpeed = 0.001;
    let lastInteractionTime = Date.now();
    const autoRotateDelay = 5000;
    let dragStartX = 0, dragStartY = 0;
    let draggingNow = false;
    const damping = 0.93;
    const defaultPitch = 0;
    let isMouseOverCanvas = false;

    canvas.addEventListener("mousedown", (e) => {
        if (!controlsEnabled) return;
        dragging = true;
        draggingNow = true;
        dragStartX = e.clientX;
        dragStartY = e.clientY;
        autoRotate = false;
        lastInteractionTime = Date.now();
    });

    canvas.addEventListener("mouseup", () => {
        if (!controlsEnabled) return;
        draggingNow = false;
    });

    canvas.addEventListener("mouseenter", () => {
        if (!controlsEnabled) return;
        isMouseOverCanvas = true;
    });

    canvas.addEventListener("mouseleave", () => {
        if (!controlsEnabled) return;
        isMouseOverCanvas = false;
        dragging = false;
    });

    canvas.addEventListener("mousemove", (e) => {
        if (!controlsEnabled) return;
        if (!draggingNow) return;
        const dx = e.clientX - dragStartX;
        const dy = e.clientY - dragStartY;
        velocityX = dx * 0.0001;
        velocityY = dy * 0.0001;
        autoRotate = false;
        lastInteractionTime = Date.now();
    });

    canvas.addEventListener("wheel", (e) => {
        if (!controlsEnabled) return;
        autoRotate = false;
        lastInteractionTime = Date.now();
        if (isMouseOverCanvas) {
            e.preventDefault();
            zoom *= e.deltaY < 0 ? 1.1 : 0.9;
            zoom = Math.max(0.9, Math.min(4.0, zoom));
        }
    }, { passive: false });

    let lastTouchDist: number | null = null;
    canvas.addEventListener("touchstart", (e) => {
        if (!controlsEnabled) return;
        autoRotate = false;
        lastInteractionTime = Date.now();
        if (e.touches.length === 2) {
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            lastTouchDist = Math.hypot(dx, dy);
        } else if (e.touches.length === 1) {
            dragging = true;
            lastX = e.touches[0].clientX;
            lastY = e.touches[0].clientY;
        }
    });

    canvas.addEventListener("touchmove", (e) => {
        if (!controlsEnabled) return;
        autoRotate = false;
        lastInteractionTime = Date.now();
        if (e.touches.length === 2) {
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            const newDist = Math.hypot(dx, dy);
            if (lastTouchDist !== null) {
                const scale = newDist / lastTouchDist;
                zoom /= scale;
                zoom = Math.max(0.1, Math.min(5.0, zoom));
            }
            lastTouchDist = newDist;
        } else if (e.touches.length === 1 && dragging) {
            const dx = e.touches[0].clientX - lastX;
            const dy = e.touches[0].clientY - lastY;
            velocityX = dx * 0.005;
            velocityY = dy * 0.005;
            lastX = e.touches[0].clientX;
            lastY = e.touches[0].clientY;
        }
    });

    canvas.addEventListener("touchend", () => {
        if (!controlsEnabled) return;
        dragging = false;
        lastTouchDist = null;
    });

    function getViewMatrix(yaw: number, pitch: number): Float32Array {
        const cy = Math.cos(yaw), sy = Math.sin(yaw);
        const cp = Math.cos(pitch), sp = Math.sin(pitch);
        return new Float32Array([
            cy, 0, -sy, 0,
            sp * sy, cp, sp * cy, 0,
            cp * sy, -sp, cp * cy, 0,
            0, 0, 0, 1,
        ]);
    }

    function resizeCanvas() {
        const windowRatio = window.innerWidth / window.innerHeight;
        const targetRatio = 16 / 9;

        let drawWidth, drawHeight;

        if (windowRatio > targetRatio) {
            // Window too wide � height fills, width letterboxed
            drawHeight = window.innerHeight;
            drawWidth = Math.floor(drawHeight * targetRatio);
        } else {
            // Window too tall � width fills, height letterboxed
            drawWidth = window.innerWidth;
            drawHeight = Math.floor(drawWidth / targetRatio);
        }

        canvas.width = drawWidth;
        canvas.height = drawHeight;

        // Center the canvas with CSS
        canvas.style.position = "absolute";
        canvas.style.left = `${(window.innerWidth - drawWidth) / 2}px`;
        canvas.style.top = `${(window.innerHeight - drawHeight) / 2}px`;

        //const size = Math.min(root.clientWidth, root.clientHeight);
        //canvas.width = size;
        //canvas.height = size;
        canvas.width = 1120;
        canvas.height = 1120;

        gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
    }
    window.addEventListener("resize", resizeCanvas);
    resizeCanvas();

    gl.clearColor(0, 0, 0, 1);
    function render() {
        if (!readyToRender) {
            requestAnimationFrame(render);
            return;
        }

        const now = Date.now();
        if (!autoRotate && now - lastInteractionTime > autoRotateDelay) {
            autoRotate = true;
        }

        if (autoRotate && !controlsEnabled) {
            yaw += autoRotateSpeed;
            const zoomLerpSpeed = 0.01;
            const pitchLerpSpeed = 0.01;
            zoom += (defaultZoom - zoom) * zoomLerpSpeed;
            pitch += (defaultPitch - pitch) * pitchLerpSpeed;
        } else {
            yaw += velocityX;
            pitch += velocityY;
            if (!draggingNow) {
                velocityX *= damping;
                velocityY *= damping;
            }
        }

        pitch = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, pitch));

        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.uniformMatrix4fv(uViewMatrix, false, getViewMatrix(yaw, pitch));
        gl.uniform1f(uZoom, 1 / zoom);
        gl.uniform1f(uAspect, canvas.width / canvas.height);

        for (let i = 0; i < 6; i++) {
            gl.drawArrays(gl.TRIANGLE_FAN, i * 4, 4);
        }

        requestAnimationFrame(render);
    }
    render();
}
